import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

const NAVY = '#1B2A4A';
const GOLD = '#B59A6A';
const GOLD_LIGHT = '#D4BC8E';
const WHITE = '#FFFFFF';
const BG = '#F2F0EB';

const STATUS_COLORS = {
  'Not Started':'#95A5A6','In Progress':'#2980B9','Waiting for Client':'#8E44AD',
  'Waiting for Counterparty':'#E67E22','Waiting for Regulator':'#D35400',
  'Under Review':'#F39C12','Completed':'#27AE60','Closed':'#7F8C8D',
};
const STATUS_TR = {
  'Not Started':'Başlamadı','In Progress':'Devam Ediyor','Waiting for Client':'Müvekkil Bekleniyor',
  'Waiting for Counterparty':'Karşı Taraf Bekleniyor','Waiting for Regulator':'Düzenleyici Bekleniyor',
  'Under Review':'İncelemede','Completed':'Tamamlandı','Closed':'Kapatıldı',
};
const PRIORITY_COLORS = { Critical:'#C0392B', High:'#E67E22', Medium:GOLD, Low:'#5D8A5E' };
const PRIORITY_TR = { Critical:'Kritik', High:'Yüksek', Medium:'Orta', Low:'Düşük' };

const fmt   = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—';
const fmtTR = d => d ? new Date(d).toLocaleDateString('tr-TR',{day:'2-digit',month:'long',year:'numeric'}) : '—';
const fmtSz = b => b<1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;
const isOver = d => d && new Date(d) < new Date();

const T = {
  en: {
    portal:'Client Portal', signout:'Sign Out', lang:'Türkçe',
    yourMatter:'Your Matter', status:'Status', priority:'Priority',
    deadline:'Target Deadline', progress:'Progress', of:'of', steps:'steps done',
    timeline:'Tasks & Timeline', documents:'Documents', messages:'Messages to Team',
    noTasks:'No tasks have been assigned yet.',
    noDocs:'No documents yet.', noMsgs:'No messages yet. Send us a message below.',
    dragDrop:'Click or drag files here to upload', uploading:'Uploading…',
    download:'Download', send:'Send', overdue:'Overdue',
    msgPlaceholder:'Write a message to your legal team…',
    welcome:'Welcome', estCompletion:'Estimated Completion',
    noMatter:'No matter has been linked to your account yet. Please contact your Koluman Law representative.',
    loading:'Loading your matter…',
  },
  tr: {
    portal:'Müvekkil Portalı', signout:'Çıkış', lang:'English',
    yourMatter:'Dosyanız', status:'Durum', priority:'Öncelik',
    deadline:'Hedef Tarih', progress:'İlerleme', of:'/', steps:'adım tamamlandı',
    timeline:'Görevler & Zaman Çizelgesi', documents:'Belgeler', messages:'Ekibe Mesajlar',
    noTasks:'Henüz görev atanmadı.',
    noDocs:'Henüz belge yok.', noMsgs:'Henüz mesaj yok. Aşağıdan mesaj gönderin.',
    dragDrop:'Yüklemek için tıklayın veya dosya sürükleyin', uploading:'Yükleniyor…',
    download:'İndir', send:'Gönder', overdue:'Süresi Geçti',
    msgPlaceholder:'Hukuk ekibinize mesaj yazın…',
    welcome:'Hoş geldiniz', estCompletion:'Tahmini Tamamlanma',
    noMatter:'Hesabınıza henüz bir dosya bağlanmamış. Lütfen Koluman Law ekibiyle iletişime geçin.',
    loading:'Dosyanız yükleniyor…',
  },
};

function Pill({ label, color, size=12 }) {
  return <span style={{ background:color+'22', color, border:`1px solid ${color}44`, borderRadius:20, padding:'3px 12px', fontSize:size, fontWeight:600 }}>{label}</span>;
}

function SectionCard({ title, children }) {
  return (
    <div style={{ background:WHITE, borderRadius:12, border:'1px solid #E5E0D8', overflow:'hidden', marginBottom:20 }}>
      <div style={{ background:NAVY, padding:'14px 20px' }}>
        <span style={{ fontFamily:'Georgia,serif', fontSize:14, fontWeight:700, color:GOLD }}>{title}</span>
      </div>
      <div style={{ padding:'20px' }}>{children}</div>
    </div>
  );
}

function Timeline({ tasks, lang }) {
  const t = T[lang];
  const fmtD = lang==='tr' ? fmtTR : fmt;
  if (!tasks.length) return <div style={{ color:'#BBB', fontSize:13, textAlign:'center', padding:'20px 0' }}>{t.noTasks}</div>;

  return (
    <div style={{ position:'relative', paddingLeft:32 }}>
      <div style={{ position:'absolute', left:11, top:10, bottom:10, width:2, background:'#E5E0D8', borderRadius:1 }} />
      {tasks.map((task) => {
        const done  = task.status==='Completed'||task.status==='Closed';
        const over  = isOver(task.deadline) && !done;
        const color = STATUS_COLORS[task.status]||'#95A5A6';
        const sLabel = lang==='tr' ? (STATUS_TR[task.status]||task.status) : task.status;
        const stDone  = (task.subtasks||[]).filter(s=>s.status==='Completed').length;
        const stTotal = (task.subtasks||[]).length;

        return (
          <div key={task.id} style={{ marginBottom:20, position:'relative' }}>
            {/* Timeline dot */}
            <div style={{ position:'absolute', left:-21, top:14, width:18, height:18, borderRadius:'50%', background:done?'#27AE60':over?'#C0392B':WHITE, border:`2.5px solid ${done?'#27AE60':over?'#C0392B':color}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {done && <div style={{ width:6, height:6, borderRadius:'50%', background:WHITE }} />}
            </div>

            <div style={{ background:'#FAFAF8', borderRadius:10, border:`1px solid ${over?'#F5C0B8':'#E5E0D8'}`, borderLeft:`3px solid ${color}`, padding:'14px 16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8, gap:8 }}>
                <div style={{ fontSize:14, fontWeight:700, color:NAVY, lineHeight:1.3 }}>{task.title}</div>
                <Pill label={sLabel} color={color} />
              </div>
              {task.description && <div style={{ fontSize:12, color:'#777', marginBottom:8, lineHeight:1.6 }}>{task.description}</div>}
              <div style={{ display:'flex', gap:16, fontSize:12, flexWrap:'wrap' }}>
                {task.deadline && <span style={{ color:over?'#C0392B':'#999', fontWeight:over?700:400 }}>📅 {fmtD(task.deadline)}{over?` · ${t.overdue}`:''}</span>}
                {stTotal>0 && <span style={{ color:'#999' }}>{stDone} / {stTotal} {t.steps}</span>}
              </div>

              {/* Subtasks */}
              {(task.subtasks||[]).length>0 && (
                <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:5 }}>
                  {task.subtasks.map(s => {
                    const sDone = s.status==='Completed';
                    const sC = STATUS_COLORS[s.status]||'#95A5A6';
                    const sL = lang==='tr' ? (STATUS_TR[s.status]||s.status) : s.status;
                    return (
                      <div key={s.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', background:sDone?'#EAF7EE':'#F5F3F0', borderRadius:6 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:sDone?'#27AE60':sC, flexShrink:0 }} />
                        <span style={{ fontSize:12, color:sDone?'#27AE60':NAVY, flex:1, textDecoration:sDone?'line-through':'none', opacity:sDone?0.7:1 }}>{s.title}</span>
                        {s.deadline && <span style={{ fontSize:11, color:'#CCC' }}>{fmtD(s.deadline)}</span>}
                        <Pill label={sL} color={sC} size={10} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DocumentsSection({ matterId, uploaderName, lang }) {
  const t = T[lang];
  const [docs, setDocs]           = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver]   = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    supabase.from('documents').select('*')
      .eq('linked_type','matter').eq('linked_id',matterId)
      .order('created_at',{ascending:false})
      .then(({data}) => data && setDocs(data));
  }, [matterId]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of Array.from(files)) {
      const path = `matter/${matterId}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g,'_')}`;
      const { error:upErr } = await supabase.storage.from('documents').upload(path, file);
      if (upErr) continue;
      const { data:doc } = await supabase.from('documents').insert([{
        linked_type:'matter', linked_id:matterId,
        file_name:file.name, file_size:file.size,
        storage_path:path, uploaded_by:uploaderName,
      }]).select().single();
      if (doc) setDocs(p=>[doc,...p]);
    }
    setUploading(false);
  };

  const download = async (doc) => {
    const { data } = await supabase.storage.from('documents').createSignedUrl(doc.storage_path, 60);
    if (data) window.location.href = data.signedUrl;
  };

  return (
    <div>
      <div onClick={()=>fileRef.current?.click()}
        onDragOver={e=>{e.preventDefault();setDragOver(true);}}
        onDragLeave={()=>setDragOver(false)}
        onDrop={e=>{e.preventDefault();setDragOver(false);upload(e.dataTransfer.files);}}
        style={{ border:`2px dashed ${dragOver?GOLD:'#D0CBBF'}`, borderRadius:8, padding:'20px', textAlign:'center', cursor:'pointer', background:dragOver?GOLD+'0A':'#FAFAF8', marginBottom:16 }}>
        <input ref={fileRef} type="file" multiple style={{display:'none'}} onChange={e=>upload(e.target.files)} />
        <div style={{fontSize:26, marginBottom:6}}>📎</div>
        <div style={{fontSize:13, color:'#888'}}>{uploading ? t.uploading : t.dragDrop}</div>
      </div>
      {docs.length===0
        ? <div style={{color:'#BBB', fontSize:13, textAlign:'center', padding:'10px 0'}}>{t.noDocs}</div>
        : docs.map(doc=>(
          <div key={doc.id} style={{display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:'1px solid #F0EDE8'}}>
            <div style={{fontSize:22}}>📄</div>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:13, fontWeight:600, color:NAVY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{doc.file_name}</div>
              <div style={{fontSize:11, color:'#AAA'}}>{fmtSz(doc.file_size)} · {doc.uploaded_by} · {fmt(doc.created_at?.slice(0,10))}</div>
            </div>
            <button onClick={()=>download(doc)} style={{background:NAVY, color:GOLD, border:'none', borderRadius:6, padding:'6px 12px', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'inherit'}}>↓ {t.download}</button>
          </div>
        ))
      }
    </div>
  );
}

function MessagesSection({ matterId, senderName, lang }) {
  const t = T[lang];
  const [notes, setNotes] = useState([]);
  const [text, setText]   = useState('');
  const fmtD = lang==='tr' ? fmtTR : fmt;

  useEffect(() => {
    supabase.from('matter_notes').select('*')
      .eq('matter_id', matterId)
      .neq('type','Internal')
      .order('created_at')
      .then(({data}) => data && setNotes(data));
  }, [matterId]);

  const send = async () => {
    if (!text.trim()) return;
    const { data } = await supabase.from('matter_notes').insert([{
      matter_id:matterId, type:'Client Message', text:text.trim(), author:senderName,
    }]).select().single();
    if (data) { setNotes(p=>[...p,data]); setText(''); }
  };

  return (
    <div>
      <div style={{display:'flex', flexDirection:'column', gap:10, marginBottom:16, maxHeight:320, overflowY:'auto', paddingRight:4}}>
        {notes.length===0
          ? <div style={{color:'#BBB', fontSize:13, textAlign:'center', padding:'20px 0'}}>{t.noMsgs}</div>
          : notes.map(n => {
            const isMe = n.author === senderName;
            return (
              <div key={n.id} style={{display:'flex', flexDirection:'column', alignItems:isMe?'flex-end':'flex-start'}}>
                <div style={{
                  maxWidth:'80%', background:isMe?NAVY:WHITE, color:isMe?GOLD_LIGHT:NAVY,
                  borderRadius:isMe?'12px 12px 2px 12px':'12px 12px 12px 2px',
                  padding:'10px 14px', fontSize:13, lineHeight:1.6,
                  border:isMe?'none':'1px solid #E5E0D8',
                }}>{n.text}</div>
                <div style={{fontSize:11, color:'#CCC', marginTop:3, padding:'0 4px'}}>{n.author} · {fmtD(n.created_at?.slice(0,10))}</div>
              </div>
            );
          })
        }
      </div>
      <div style={{display:'flex', gap:8}}>
        <input value={text} onChange={e=>setText(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&send()}
          placeholder={t.msgPlaceholder}
          style={{flex:1, border:'1px solid #D0CBBF', borderRadius:8, padding:'10px 14px', fontSize:13, fontFamily:'inherit', background:'#FAFAF8', outline:'none'}} />
        <button onClick={send} style={{background:NAVY, color:GOLD, border:'none', borderRadius:8, padding:'10px 20px', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit'}}>{t.send}</button>
      </div>
    </div>
  );
}

export default function ClientPortal({ session }) {
  const [lang,    setLang]    = useState('en');
  const [matter,  setMatter]  = useState(null);
  const [tasks,   setTasks]   = useState([]);
  const [tab,     setTab]     = useState('timeline');
  const [loading, setLoading] = useState(true);
  const t = T[lang];
  const email = session.user?.email || '';

  useEffect(() => {
    const load = async () => {
      const { data:matters } = await supabase
        .from('matters')
        .select('*, tasks(*, subtasks(*))')
        .eq('client_email', email)
        .limit(1);
      if (matters?.length) {
        const m = matters[0];
        setMatter(m);
        setTasks([...(m.tasks||[])].sort((a,b)=>(!a.deadline?1:!b.deadline?-1:new Date(a.deadline)-new Date(b.deadline))));
      }
      setLoading(false);
    };
    load();
  }, [email]);

  const handleSignOut = () => supabase.auth.signOut();

  const completedTasks = tasks.filter(t=>t.status==='Completed'||t.status==='Closed').length;
  const pct = tasks.length ? Math.round((completedTasks/tasks.length)*100) : 0;
  const statusLabel   = lang==='tr' ? (STATUS_TR[matter?.status]||matter?.status) : matter?.status;
  const priorityLabel = lang==='tr' ? (PRIORITY_TR[matter?.priority]||matter?.priority) : matter?.priority;
  const clientName    = matter?.client || email;
  const fmtD          = lang==='tr' ? fmtTR : fmt;

  if (loading) return (
    <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100vh', background:BG, flexDirection:'column', gap:16}}>
      <div style={{fontFamily:'Georgia,serif', fontSize:24, fontWeight:700, color:NAVY}}>Koluman<span style={{color:GOLD}}>Law</span></div>
      <div style={{width:28, height:28, border:`3px solid ${GOLD}33`, borderTop:`3px solid ${GOLD}`, borderRadius:'50%', animation:'spin 0.8s linear infinite'}} />
      <div style={{fontSize:13, color:'#888'}}>{t.loading}</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (!matter) return (
    <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100vh', background:BG, flexDirection:'column', gap:20, padding:32}}>
      <div style={{fontFamily:'Georgia,serif', fontSize:28, fontWeight:700, color:NAVY}}>Koluman<span style={{color:GOLD}}>Law</span></div>
      <div style={{fontSize:14, color:'#888', textAlign:'center', maxWidth:360, lineHeight:1.8}}>{t.noMatter}</div>
      <button onClick={handleSignOut} style={{background:NAVY, color:GOLD, border:'none', borderRadius:7, padding:'10px 24px', cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit'}}>{t.signout}</button>
    </div>
  );

  const tabs = [
    { key:'timeline',  label:t.timeline },
    { key:'documents', label:t.documents },
    { key:'messages',  label:t.messages },
  ];

  return (
    <div style={{minHeight:'100vh', background:BG, fontFamily:"'Segoe UI', system-ui, sans-serif"}}>
      {/* Header */}
      <div style={{background:NAVY, borderBottom:`2px solid ${GOLD}33`}}>
        <div style={{maxWidth:880, margin:'0 auto', padding:'0 24px', display:'flex', alignItems:'center', height:62, gap:16}}>
          <div style={{fontFamily:'Georgia,serif', fontSize:20, fontWeight:700, color:WHITE, flex:1}}>
            Koluman<span style={{color:GOLD}}>Law</span>
            <span style={{fontSize:10, color:GOLD_LIGHT, letterSpacing:2.5, marginLeft:14, textTransform:'uppercase', fontWeight:400}}>{t.portal}</span>
          </div>
          <button onClick={()=>setLang(l=>l==='en'?'tr':'en')} style={{background:'none', border:`1px solid ${GOLD}44`, color:GOLD, borderRadius:6, padding:'5px 14px', fontSize:11, cursor:'pointer', fontFamily:'inherit'}}>{t.lang}</button>
          <div style={{fontSize:11, color:'#6A7A9A', maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{email}</div>
          <button onClick={handleSignOut} style={{background:'none', border:'1px solid #2D3F66', color:'#6A7A9A', borderRadius:6, padding:'5px 12px', fontSize:11, cursor:'pointer', fontFamily:'inherit'}}>{t.signout}</button>
        </div>
      </div>

      <div style={{maxWidth:880, margin:'0 auto', padding:'32px 24px'}}>
        {/* Welcome */}
        <div style={{marginBottom:24}}>
          <div style={{fontSize:12, color:GOLD, fontWeight:600, marginBottom:4, letterSpacing:0.5}}>{t.welcome}, {clientName}</div>
          <div style={{fontFamily:'Georgia,serif', fontSize:28, fontWeight:700, color:NAVY, lineHeight:1.2}}>{matter.name}</div>
        </div>

        {/* Overview card */}
        <div style={{background:WHITE, borderRadius:14, border:'1px solid #E5E0D8', overflow:'hidden', marginBottom:24}}>
          <div style={{height:3, background:`linear-gradient(90deg, ${NAVY}, ${GOLD})`}} />
          <div style={{padding:'24px'}}>
            <div style={{display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20, marginBottom:20}}>
              {[
                [t.status,   <Pill label={statusLabel} color={STATUS_COLORS[matter.status]||GOLD} />],
                [t.priority, <Pill label={priorityLabel} color={PRIORITY_COLORS[matter.priority]||GOLD} />],
                [t.deadline, <span style={{fontSize:13, fontWeight:600, color:isOver(matter.deadline)?'#C0392B':NAVY}}>{fmtD(matter.deadline)}</span>],
                [t.progress, null],
              ].map(([label, val], i) => (
                <div key={i}>
                  <div style={{fontSize:10, color:'#AAA', textTransform:'uppercase', letterSpacing:1, marginBottom:8}}>{label}</div>
                  {val || (
                    <div>
                      <div style={{display:'flex', justifyContent:'space-between', fontSize:12, color:'#888', marginBottom:5}}>
                        <span>{pct}%</span><span>{completedTasks}/{tasks.length}</span>
                      </div>
                      <div style={{height:6, background:'#F0EDE8', borderRadius:3}}>
                        <div style={{height:'100%', width:`${pct}%`, background:pct===100?'#27AE60':GOLD, borderRadius:3, transition:'width 0.5s'}} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {matter.description && <div style={{fontSize:13, color:'#666', lineHeight:1.8, borderTop:'1px solid #F0EDE8', paddingTop:16}}>{matter.description}</div>}
          </div>
        </div>

        {/* Tabs */}
        <div style={{display:'flex', borderBottom:'1px solid #E5E0D8', marginBottom:24}}>
          {tabs.map(tb => (
            <button key={tb.key} onClick={()=>setTab(tb.key)} style={{
              padding:'11px 24px', border:'none', background:'none', cursor:'pointer',
              fontSize:13, fontWeight:tab===tb.key?700:400, color:tab===tb.key?NAVY:'#888',
              fontFamily:'inherit', borderBottom:tab===tb.key?`2px solid ${GOLD}`:'2px solid transparent',
            }}>{tb.label}</button>
          ))}
        </div>

        {/* Content */}
        <SectionCard title={tabs.find(tb=>tb.key===tab)?.label}>
          {tab==='timeline'  && <Timeline tasks={tasks} lang={lang} />}
          {tab==='documents' && <DocumentsSection matterId={matter.id} uploaderName={clientName} lang={lang} />}
          {tab==='messages'  && <MessagesSection matterId={matter.id} senderName={clientName} lang={lang} />}
        </SectionCard>

        <div style={{textAlign:'center', marginTop:12, fontSize:11, color:'#CCC', lineHeight:1.8}}>
          Koluman Law · Legal Services Since 1988<br/>
          {lang==='tr' ? 'Bu portal gizli ve güvenlidir.' : 'This portal is private and secure.'}
        </div>
      </div>
    </div>
  );
}
